/**
 * Counter class allows to add countdown timers
 * on the website.
 *
 * @package Embark Shopify Theme
 */
export class Counter {
  /**
   *
   * @param expires_at A Unix timestamp
   * @param time_now
   */
  constructor(expires_at, time_now = null) {
    this.events = { finish: [], tick: [] };

    this.expires_at = parseInt(expires_at) * 1000;
    this.time_now = time_now ? parseInt(time_now) * 1000 : Date.now();

    // To improve performance, keep track of
    // current values and only update if they chang
    this.days = 0;
    this.hours = 0;
    this.minutes = 0;
    this.seconds = 0;
  }

  /**
   * Bind the counter to an element.
   *
   * @param selector
   * @return {Counter}
   */
  bindTo(selector) {
    this.element = document.querySelector(selector);
    return this;
  }

  /**
   * Start the counter.
   *
   * @return {*}
   */
  start() {
    if (typeof this.interval !== 'undefined') {
      clearInterval(this.interval);
    }

    if (this.element) {
      // add the HTML
      const fragment = document.createDocumentFragment();

      ['days', 'hours', 'minutes', 'seconds'].forEach((key) => {
        const span = document.createElement('span');
        span.classList.add(key);
        fragment.appendChild(span); // not an actual DOM update.
      });

      this.element.appendChild(fragment);
    }

    this.interval = setInterval(this.tick.bind(this), 1000);

    return this;
  }

  /**
   * Counter tick, called every second.
   *
   * @return void
   */
  tick() {
    // Increment the time.
    this.time_now = this.time_now + 1000;

    // Find the distance between now and the count down date
    const distance = this.expires_at - this.time_now;

    // Time calculations for days, hours, minutes and seconds
    const DAY_IN_MS = 1000 * 60 * 60 * 24;
    const HOUR_IN_MS = 1000 * 60 * 60;
    const MINUTE_IN_MS = 1000 * 60;

    const days = Math.floor(distance / DAY_IN_MS);
    const hours = Math.floor((distance % DAY_IN_MS) / HOUR_IN_MS);
    const minutes = Math.floor((distance % HOUR_IN_MS) / MINUTE_IN_MS);
    const seconds = Math.floor((distance % MINUTE_IN_MS) / 1000);

    this.current_tick = {
      days,
      hours,
      minutes,
      seconds,
    };

    /**
     * Fire the `tick` event, might useful when the consumers
     * want to perform certain actions based where countdown has reached.
     */
    this.fire('tick', { days, hours, minutes, seconds });

    if (this.element) {
      this.render();
    }

    /**
     * If the count down is over,
     * clear the interval and fire `finish` event so that the consumers
     * can perform actions specific to their needs.
     */
    if (distance < 0) {
      clearInterval(this.interval);
      this.fire('finish');
    }
  }

  /**
   * Render the counter if bound to an element.
   *
   * @return Counter
   */
  render() {
    const { days, hours, minutes, seconds } = this.current_tick;

    const template_args = {
      'Day|Days': days,
      'Hour|Hours': hours,
      'Minute|Minutes': minutes,
      'Second|Seconds': seconds,
    };

    Object.keys(template_args).forEach((arg) => {
      const value = template_args[arg];
      const [singular, plural] = arg.split('|');

      if (!value || value < 0) {
        return;
      }

      const key = plural.toLowerCase();

      // Perform DOM updates only if the value has changed.
      if (this[key] !== value) {
        this[key] = value;
        this.element.querySelector(`.${key}`).innerText = `${value} ${
          value > 1 ? plural : singular
        } `;
      }
    });

    return this;
  }

  /**
   * Subscribe to a counter event
   * @param event can be one of [tick, finish]
   * @param callback
   * @return {Counter}
   */
  on(event, callback) {
    this.events[event].push(callback);

    return this;
  }

  /**
   * Publish a counter event.
   *
   * @param event
   * @param parameters
   */
  fire(event, ...parameters) {
    // console.log('Firing => ' + event);
    this.events[event].forEach((callback) => {
      callback(...parameters);
    });
  }
}
